import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { observer, inject } from 'mobx-react';

import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import compose from 'helpers/compose';
import CustomPaper from 'components/Layout/Background';
import ControlButtons from 'components/ControlButtons';

import { RootStore } from 'stores/rootStore';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { AdjustmentStore } from 'stores/account/withdrawMoney/adjustmentStore';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';

import NewReserves from 'pages/Accounts/Edit/components/Reserves';
import AdditionalTargets from 'pages/Accounts/Edit/components/AdditionalTargets';
import Deposits from 'pages/Accounts/Edit/components/Deposits';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { Flows } from 'pages/Accounts/Detail/enums';

interface Props {
  rootStore: RootStore;
  accountDetailStore: AccountDetailStore;
}

const Adjustments: React.FC<Props> = ({ rootStore, accountDetailStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const [store] = useState(new AdjustmentStore(rootStore, accountDetailStore, accountId));
  const { currency } = accountDetailStore;
  const flow = accountDetailStore.flow?.id;

  useEffect(() => {
    store.fetchAllData();
  }, [accountId, store]);

  const refreshAdjustments = () => {
    store.fetchAllData();
  };

  return store.allData ? (
    <Container maxWidth="md">
      <CustomPaper>
        {flow === Flows.WITHDRAWAL && (
          <Typography variant="h1">{t('accountDetail_moneyWithdrawal_info')}</Typography>
        )}
        <NewReserves
          viewMode={flow === Flows.WITHDRAWAL ? ViewMode.WITHDRAW : ViewMode.TRANSFER}
          currency={currency}
          reservesData={store.allData.reserveData}
          refresh={refreshAdjustments}
          accountId={accountId}
          moduleInfo={t('accountDetail_withdrawal_adjustRent')}
        />
        <Box mt={4} />
        <AdditionalTargets
          accountId={accountId}
          targetsOnly={true}
          targetsData={store.allData.targetData}
          refresh={refreshAdjustments}
          currency={currency}
          viewMode={flow === Flows.WITHDRAWAL ? ViewMode.WITHDRAW : ViewMode.TRANSFER}
          moduleInfo={t('accountDetail_withdrawal_adjustTargets')}
          defaultInflation={store.allData.options.defaultInflationRate}
          defaultInflationType={store.allData.options.defaultInflationType}
        />
        <Box mt={4} />
        <Deposits
          accountId={accountId}
          currency={currency}
          fullWidth={true}
          viewMode={flow === Flows.WITHDRAWAL ? ViewMode.WITHDRAW : ViewMode.TRANSFER}
          data={store.allData.depositData}
          refresh={refreshAdjustments}
          defaultInflation={store.allData.options.defaultInflationRate}
          defaultInflationType={store.allData.options.defaultInflationType}
        />
        <ControlButtons
          onNext={() => accountDetailStore.goFwd()}
          onBack={() => accountDetailStore.goBack()}
          isSubmitting={false}
          nextText={t(accountDetailStore.nextStepLabel)}
          backText={t(accountDetailStore.prevStepLabel)}
        />
      </CustomPaper>
    </Container>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Adjustments);
